import { API, GraphQLQuery } from "@aws-amplify/api";
import { useQuery } from "@tanstack/react-query";
import { createContext, FC, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { BehaviorSubject } from "rxjs";
import { Button, Container, Grid, List } from "semantic-ui-react";
import { AccountDropdown, accountId } from "../accounts";
import {
  DeleteAccountToResourceMutation,
  DoScanMutation,
  InitScanMutation,
  ListAccountToResourcesQuery,
  ScanStatus,
  Summary
} from "../API";
import { deleteAccountToResource, deleteUserInfo, doScan, initScan } from "../graphql/mutations";
import { listAccountToResources } from "../graphql/queries";
import { useBehaviorSubject } from "../hooks";
import { NavBar } from "./NavBar";
import { ScanResult } from "./ScanResult";
import { RenderAccountQuota } from "./Profile";

export function useScanHistory() {
  const aid = useContext(accountId)
  return useQuery({
    queryKey: [listAccountToResources, aid],
    queryFn: async () => aid ? (await API.graphql<GraphQLQuery<ListAccountToResourcesQuery>>({
      query: listAccountToResources,
      variables: {
        accountId: aid,
        resourceId: {
          beginsWith: 'ec2_summary_'
        }
      }
    })).data : null
  })
}

export const scanIndex = createContext<string>("")

export const ScanHistory: FC =
  () => {
    const aid = useContext(accountId)
    const si = useContext(scanIndex)
    const [clicked, setClicked] = useBehaviorSubject(useMemo(() => new BehaviorSubject(false), [aid, si]))
    const {isLoading, isFetching, data, refetch} = useScanHistory()
    const navigate = useNavigate()
    const [creatingNewScan, setCreatingNewScan] = useState(false)
    const [resources, setResources] = useState('ec2') // to add more
    const getScanSummary = (summary?: Summary | null) => {
      if (!summary) return 'No data'
      if (summary.status === ScanStatus.NONE) return 'Not Started'
      if (summary.status === ScanStatus.INPROGRESS) return 'Scan In Progress'
      return `${summary.idleCount} Idle / ${summary.scannedCount} Found`
    }

    // empty for no deleting, or successfully deleted
    // resourceId when deleting in progress
    // any string for delete failure
    const [deletingSummary, setDeletingSummary] = useState('')
    const deleteScan = async (accountId: string, resourceId: string) => {
      try {
        setDeletingSummary(resourceId)
        await API.graphql<GraphQLQuery<DeleteAccountToResourceMutation>>({
          query: deleteAccountToResource,
          variables: { input: {
            accountId: accountId,
            resourceId: resourceId
          }}
        })
        setDeletingSummary('')
        refetch()
      } catch (error: any) {
        // show alert box
      }
    }

    return (
      <div style={{
        flexDirection: 'column',
        justifyContent: 'left',
        alignItems: 'flex-start'
      }}>
        <Button.Group>
          <Button
            icon='refresh'
            disabled={creatingNewScan}
            loading={isLoading || (clicked && isFetching)}
            onClick={() => {
              if (isLoading) return
              setClicked(true)
              refetch()
            }}
          />
          <Button
            loading={creatingNewScan}
            onClick={async () => {
              if (creatingNewScan) return
              setCreatingNewScan(true)
              try {
                const initScanResp = await API.graphql<GraphQLQuery<InitScanMutation>>({
                  query: initScan,
                  variables: {accountId: aid, resources}
                })
                await refetch()

                // trigger async scans for each resouce type
                const { scanIndex } = JSON.parse(initScanResp?.data?.initScan?.body || '')
                if (!scanIndex) {
                  // alert error
                }
                await resources.split(',').map(s=>s.trim()).forEach(resId => 
                  API.graphql<GraphQLQuery<DoScanMutation>>({
                    query: doScan,
                    variables: { accountId: aid, resourceId: `${resId}_smmary_${scanIndex}` }
                  }));
              } finally {
                setCreatingNewScan(false)
              }
            }}
          >New Scan</Button>
        </Button.Group>
        {(aid?.length === 0) && <div>Please select your account</div> }
        {aid?.length > 0 && !data?.listAccountToResources?.items && <div>No scans yet</div>}
        {!!data?.listAccountToResources?.items && <List link>
          {Array.from(Array(data?.listAccountToResources?.items
            // sort by the resourceId
            .sort((a, b) => parseInt(b?.resourceId?.split('_')?.pop() || '0') - parseInt(a?.resourceId?.split('_')?.pop() || '0'))
            .map((item, idx) => {
            const index = parseInt(item?.resourceId?.split('_')?.pop() || idx + '')
            return (
              <List.Item active={si === `${index}`} key={index}>
                <List.Header
                  as='a'
                  onClick={() => navigate(`/accounts/${aid}/scans/${index}`)}
                  key={index}>Scan #{index}</List.Header>
                <List.Content floated="right">
                  <Button icon={'trash'} size="mini" loading={deletingSummary === item?.resourceId}
                    onClick={() => deleteScan(aid!, item?.resourceId!)}/>
                </List.Content>
                <List.Content>
                  {getScanSummary(item?.summary)}<br/>
                  Scanned at {new Date(item?.createdAt!).toUTCString()}
                </List.Content>
              </List.Item>
            )
          })))}
        </List>}
      </div>
    )
  }


export const Dashboard: FC = () => {
  const aid = useContext(accountId)
  return <Container>
    <NavBar/>
    <AccountDropdown navigatePath="/accounts/ACCOUNT_ID/scans"/>
    { aid && <div><RenderAccountQuota accountId={aid}/></div> }
    <Grid>
      <Grid.Row>
        <Grid.Column width={4}>
          <ScanHistory/>
        </Grid.Column>
        <Grid.Column width={12}>
          <ScanResult/>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  </Container>
}